import _ from 'underscore';
import { useContext, useEffect, useState, useCallback } from 'react';

import { ScopeContext } from '@bingads-webui-react/scope-service';

import { fetchLocationData } from './fetch-location-data';
import { isUSorCanada } from '../utils/is-us-or-canada';
import { filterLocationData } from '../utils/filter-location-data';
import { formatLocationData } from '../utils/format-location-data';
import { IScopeContext, LocationData } from '../utils/types';
import { locationGranularity } from '../utils/const';

export const useTopLocationsData = ({
  metric,
  dateRange,
  i18n,
  willFetchData,
  willRender,
}) => {
  const { scope } = useContext(ScopeContext) as IScopeContext;
  const {
    values: {
      cid,
      aid,
      campaignId,
    },
  } = scope;

  const [locations, setLocations] = useState<LocationData[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const setTopLocationValues = useCallback((locationData, granularity) => {
    setLocations(formatLocationData({
      locationData, granularity, metric, dateRange, i18n,
    }));
    setIsLoading(false);
    willRender();
  }, [dateRange, i18n, metric, willRender]);

  useEffect(() => {
    setIsLoading(true);
    const {
      fetchCountryData,
      fetchStateOrProvinceData,
      fetchCountyData,
      fetchCityData,
      fetchPostalCodeData,
    } = fetchLocationData({
      cid, aid, campaignId, dateRange, metric,
    });
    willFetchData();
    fetchCountryData.then((countryData) => {
      const countryLocationData = filterLocationData({ data: countryData?.value, granularity: locationGranularity.country, metric });
      if (_.isEmpty(countryLocationData)) {
        setTopLocationValues([], locationGranularity.country);
        return;
      }
      if (_.size(countryLocationData) > 1 && !isUSorCanada(countryLocationData)) {
        setTopLocationValues(countryLocationData, locationGranularity.country);
        return;
      }

      fetchStateOrProvinceData.then((stateOrProvinceData) => {
        const stateOrProvinceLocationData = filterLocationData({ data: stateOrProvinceData?.value, granularity: locationGranularity.stateOrProvince, metric });
        if (_.size(stateOrProvinceLocationData) > 1) {
          setTopLocationValues(stateOrProvinceLocationData, locationGranularity.stateOrProvince);
          return;
        }
        fetchCountyData.then((countyData) => {
          const countyLocationData = filterLocationData({ data: countyData?.value, granularity: locationGranularity.county, metric });
          if (_.size(countyLocationData) > 1) {
            setTopLocationValues(countyLocationData, locationGranularity.county);
            return;
          }
          fetchCityData.then((cityData) => {
            const cityLocationData = filterLocationData({ data: cityData?.value, granularity: locationGranularity.city, metric });

            if (_.size(cityLocationData) > 1) {
              setTopLocationValues(cityLocationData, locationGranularity.city);
              return;
            }
            fetchPostalCodeData.then((postalCodeData) => {
              const postalCodeLocationData = filterLocationData({ data: postalCodeData?.value, granularity: locationGranularity.postalCode, metric });
              if (_.isEmpty(postalCodeLocationData)) {
                // We can have less data when we drill down the granularity, since we might not have enough granularity for particular data.
                // In this case, we just go upper granularities until we find one with data.
                if (!_.isEmpty(cityLocationData)) {
                  setTopLocationValues(cityLocationData, locationGranularity.city);
                  return;
                }
                if (!_.isEmpty(countyLocationData)) {
                  setTopLocationValues(countyLocationData, locationGranularity.county);
                  return;
                }
                if (!_.isEmpty(stateOrProvinceLocationData)) {
                  setTopLocationValues(stateOrProvinceLocationData, locationGranularity.stateOrProvince);
                  return;
                }
                setTopLocationValues(countryLocationData, locationGranularity.country);
                return;
              }
              setTopLocationValues(postalCodeLocationData, locationGranularity.postalCode);
            });
          });
        });
      });
    });
  }, [aid, campaignId, cid, dateRange, metric, setTopLocationValues, willFetchData]);

  return { isLoading, locations };
};
